import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionScreen } from '../../../features/flow/session';
import type { ScreenQuestion } from '../../../features/flow/types';
import { cleanParagraph, cleanTitle } from '../../../features/flow/utils';
import {
  AEAT_METHOD_OPTION_CERTIFICATE,
  AEAT_METHOD_OPTION_CERTIFICATE_ES,
  AEAT_METHOD_OPTION_CLAVE,
  AEAT_METHOD_OPTION_IBAN,
  AEAT_METHOD_OPTION_REFERENCE,
  AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN,
  AEAT_SIGN_IN_OPTIONS_FULL,
  AEAT_METHOD_OPTION_NEED_HELP,
  aeatSignInCanonicalOptions,
  withoutAeatCertificateOption,
  resolveCanonicalAeatMethod,
  getAeatMethodHelpLines,
  getAeatMethodOptionEmoji,
  isAeatMethodUploadOrIbanNoLiveConnect,
  openAeatAccessHelpInNewTab,
  uiLabelToBffFlow,
  AEAT_DEFAULT_PDP,
} from '../../../features/aeat/aeatMethodMapping';
import {
  aeatClavePin,
  aeatClaveResend,
  aeatClaveStart,
  aeatCreateSession,
  aeatFiscalData,
  aeatGetSession,
  aeatRatification,
  aeatReference,
  formatAeatUserFacingError,
  parseFastApiError,
  type FiscalDataResponse,
} from '../../../features/aeat/aeatFiscalApi';
// @ts-ignore — JS module
import { useCertLogin } from '../../../features/aeat/cert_auth/useCertLogin';
import { capture } from '../../../shared/posthog';
import { InfoCircle } from 'iconoir-react';

type Phase =
  | 'form'
  | 'ratification_block'
  | 'cert_fetch'
  | 'clave_identity'
  | 'clave_pin'
  | 'reference_fetch'
  | 'success';

type ClaveIdentityDocKind = 'dni' | 'nie';

function navLikeParagraph(p: string): boolean {
  if (/^Back\b.*\bContinue$/i.test(p)) return true;
  if (/^Back$/i.test(p) || /^Continue$/i.test(p)) return true;
  return false;
}

/** Matches info icon column so rows without an icon stay the same width */
const AEAT_METHOD_INFO_COL_CLASS = 'w-5 shrink-0';

function AeatMethodHelpBubble({ lines, open }: { lines: string[]; open: boolean }) {
  if (!open) return null;
  return (
    <div
      className={[
        'pointer-events-auto absolute top-1/2 z-[60] flex w-max max-w-[min(280px,calc(100vw-2.5rem))] -translate-y-1/2 items-center',
        // < lg: bubble opens to the left so it stays on-screen (phones & most tablets)
        'right-full mr-1 flex-row-reverse',
        'lg:left-full lg:right-auto lg:ml-1 lg:mr-0 lg:flex-row',
      ].join(' ')}
      role="tooltip"
    >
      <span
        className="h-0 w-0 shrink-0 border-y-[5px] border-l-[6px] border-y-transparent border-l-[#2D2D2D] lg:border-l-0 lg:border-r-[6px] lg:border-r-[#2D2D2D]"
        aria-hidden
      />
      <div className="min-w-0 max-w-full rounded-lg bg-[#2D2D2D] px-3 py-2.5 text-[13px] leading-snug text-white shadow-lg">
        {lines.map((line, i) => (
          <p key={i} className={i > 0 ? 'mt-2' : undefined}>
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

/** Stripped Figma/sidebar labels that duplicate the radio list or title — not real body copy. */
const AEAT_PARAGRAPH_NOISE_LABELS: readonly string[] = [
  AEAT_METHOD_OPTION_CERTIFICATE,
  AEAT_METHOD_OPTION_CERTIFICATE_ES,
  AEAT_METHOD_OPTION_CLAVE,
  AEAT_METHOD_OPTION_REFERENCE,
  AEAT_METHOD_OPTION_IBAN,
  AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN,
  AEAT_METHOD_OPTION_NEED_HELP,
];

function isRedundantAeatConnectBlurb(paragraph: string, screenTitle: string): boolean {
  const title = cleanTitle(screenTitle).trim();
  if (!title) return false;
  const tl = title.toLowerCase();
  if (!/\b(aeat|agencia|tributaria|connect|conéctate|conectar|import)\b/i.test(tl)) return false;
  const pl = paragraph.trim().toLowerCase();
  if (/^(connect with|conectar con)\b/i.test(pl) && /\b(aeat|agencia tributaria)\b/i.test(pl)) {
    return true;
  }
  return false;
}

function introParagraphsForAeat(sessionScreen: SessionScreen, optionLabels: string[]): string[] {
  const heading = cleanTitle(sessionScreen.title ?? '');
  const optionSet = new Set(
    [...optionLabels, ...AEAT_PARAGRAPH_NOISE_LABELS]
      .map((o) => cleanParagraph(String(o)).trim())
      .filter(Boolean)
  );
  return (sessionScreen.paragraphs ?? [])
    .map((p) => cleanParagraph(String(p)).trim())
    .filter(Boolean)
    .filter((p) => !navLikeParagraph(p))
    .filter((p) => p !== heading)
    .filter((p) => !optionSet.has(p))
    .filter((p) => !/^IBAN\b/i.test(p))
    .filter((p) => !isRedundantAeatConnectBlurb(p, sessionScreen.title ?? ''));
}

export function AeatConnectionScreenBody({
  sessionScreen,
  question,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  isSubmitting,
  connectRef,
  onPhaseChange,
}: {
  sessionScreen: SessionScreen;
  question?: ScreenQuestion | null;
  answers: Record<string, unknown>;
  onAnswerChange: (id: string, value: unknown) => void;
  onNext: (answersPatch?: Record<string, unknown>) => void;
  onBack: () => void;
  isSubmitting?: boolean;
  connectRef?: React.MutableRefObject<{ promptThenConnect: () => void } | null>;
  onPhaseChange?: (phase: string) => void;
}) {
  const { t } = useTranslation('common');

  const options: string[] = (() => {
    const raw =
      !Array.isArray(question?.options) || (question!.options as string[]).length === 0
        ? [...AEAT_SIGN_IN_OPTIONS_FULL]
        : (question!.options as string[]).map(String).filter(Boolean);
    return withoutAeatCertificateOption(raw);
  })();

  const canonicalOptions = useMemo(() => aeatSignInCanonicalOptions(), []);

  // Use ALL question option labels when filtering paragraphs
  const allOptionLabels: string[] = Array.isArray(question?.options)
    ? (question!.options as string[]).map(String)
    : options;

  const intro = introParagraphsForAeat(sessionScreen, allOptionLabels);
  const title = cleanTitle(sessionScreen.title ?? '');

  // ── Wizard state ──────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('form');
  const [method, setMethod] = useState<string>(() =>
    resolveCanonicalAeatMethod(
      String(
        (answers.aeat_method as string) || (answers.connection_sign_in_to_aeat as string) || ''
      ),
      options
    )
  );
  const [nif, setNif] = useState<string>(String((answers.aeat_nif as string) || (answers.nif as string) || ''));
  const [ibanLast5, setIbanLast5] = useState<string>(() => {
    const raw = answers.aeat_iban_last5;
    return typeof raw === 'string' ? raw.replace(/\D/g, '').slice(0, 5) : '';
  });
  const ejercicio = useMemo(() => {
    const ty = answers.tax_year;
    if (ty != null && ty !== '') return String(ty).trim();
    const stored = answers.aeat_ejercicio;
    if (stored != null && String(stored).trim() !== '') return String(stored).trim();
    return String(new Date().getFullYear());
  }, [answers.tax_year, answers.aeat_ejercicio]);
  const [aeatSessionId, setAeatSessionId] = useState<string | null>(
    (answers.aeat_session_id as string) || null
  );
  const [ratificationUrls, setRatificationUrls] = useState<Record<string, string> | null>(null);
  const [certPassword, setCertPassword] = useState<string>('');
  const [claveFecha, setClaveFecha] = useState<string>('');
  const [claveSoporte, setClaveSoporte] = useState<string>('');
  const [claveIdentityDocKind, setClaveIdentityDocKind] = useState<ClaveIdentityDocKind | null>(null);
  const [claveMovilHint, setClaveMovilHint] = useState<string>('');
  const [clavePin, setClavePin] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pinSecondsLeft, setPinSecondsLeft] = useState<number | null>(null);
  const pinTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [prefillSummary, setPrefillSummary] = useState<{
    fieldCount: number;
    hasName: boolean;
    hasAddress: boolean;
    hasIncome: boolean;
    discrepancyCount: number;
  } | null>(null);
  const [parseIncompleteReason, setParseIncompleteReason] = useState<
    'mock' | 'parse_failed' | null
  >(null);
  const [preConnectModalOpen, setPreConnectModalOpen] = useState(false);
  const preConnectDialogRef = useRef<HTMLDivElement | null>(null);
  const [openHelpLabel, setOpenHelpLabel] = useState<string | null>(null);
  const prevPhaseRef = useRef<Phase>(phase);

  useEffect(() => {
    if (phase === 'clave_identity' && prevPhaseRef.current !== 'clave_identity') {
      setClaveIdentityDocKind(null);
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  // ── Ratification auto-polling state ────────────────────────────────────────
  const [ratPollingActive, setRatPollingActive] = useState(false);
  const [ratificationJustPassed, setRatificationJustPassed] = useState(false);
  const ratPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ratPollCountRef = useRef(0);
  const RAT_POLL_INTERVAL_MS = 10_000;
  const RAT_POLL_MAX_ATTEMPTS = 18;

  const busy = loading || Boolean(isSubmitting);

  const { login: certLogin, isLoading: certLoading, error: certError, isAuthenticated: certAuthenticated, nif: certNif, fullName: certFullName } = useCertLogin();

  // When certificate method is clicked, immediately trigger the browser cert picker
  const handleMethodClick = async (canonical: string) => {
    if (canonical === AEAT_METHOD_OPTION_NEED_HELP) {
      openAeatAccessHelpInNewTab();
      return;
    }
    setMethod(canonical);
    if (canonical === AEAT_METHOD_OPTION_IBAN) {
      const raw = answers.aeat_iban_last5;
      setIbanLast5(typeof raw === 'string' ? raw.replace(/\D/g, '').slice(0, 5) : '');
    } else {
      setIbanLast5('');
    }
    if (canonical === AEAT_METHOD_OPTION_CERTIFICATE) {
      try {
        const result = await certLogin() as { nif?: string } | null;
        if (result?.nif) {
          setNif(result.nif);
          onAnswerChange('aeat_nif', result.nif);
        }
      } catch {
        // error is surfaced via certError from the hook
      }
    }
  };

  const fiscalPayload = () => {
    let flowSid: string | undefined;
    try {
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('flow_session_id') : null;
      if (stored && stored.trim()) flowSid = stored.trim();
    } catch {
      flowSid = undefined;
    }
    return {
      nif: nif.trim().toUpperCase(),
      pdp: AEAT_DEFAULT_PDP,
      flow_answers: answers && Object.keys(answers).length > 0 ? answers : undefined,
      flow_session_id: flowSid,
    };
  };

  // ── PIN timer ─────────────────────────────────────────────────────────────
  const startPinTimer = useCallback((smsRequestedAt?: string) => {
    if (pinTimerRef.current) clearInterval(pinTimerRef.current);
    const start = smsRequestedAt ? new Date(smsRequestedAt).getTime() : Date.now();
    const PIN_TTL_MS = 180_000;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((start + PIN_TTL_MS - Date.now()) / 1000));
      setPinSecondsLeft(remaining);
      if (remaining <= 0 && pinTimerRef.current) {
        clearInterval(pinTimerRef.current);
        pinTimerRef.current = null;
      }
    };
    update();
    pinTimerRef.current = setInterval(update, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (pinTimerRef.current) clearInterval(pinTimerRef.current);
    };
  }, []);

  // ── Session recovery on page refresh ──────────────────────────────────────
  useEffect(() => {
    const existingId = answers.aeat_session_id as string | undefined;
    if (!existingId || phase !== 'form') return;
    let cancelled = false;
    (async () => {
      try {
        const sess = await aeatGetSession(existingId);
        if (cancelled) return;
        setAeatSessionId(existingId);
        const m =
          (answers.aeat_method as string) || (answers.connection_sign_in_to_aeat as string) || '';
        if (m) {
          const resolved = resolveCanonicalAeatMethod(m, options);
          setMethod(resolved);
          if (resolved === AEAT_METHOD_OPTION_IBAN) {
            const raw = answers.aeat_iban_last5;
            setIbanLast5(typeof raw === 'string' ? raw.replace(/\D/g, '').slice(0, 5) : '');
          }
        }
        const stepMap: Record<string, Phase> = {
          clave_sms_sent: 'clave_pin',
          clave_pin_ok: 'success',
          clave_www12_ok: 'clave_identity',
          reference_ok: 'success',
          fiscal_fetched: 'success',
        };
        if (sess.flow === 'certificate' && sess.step === 'ratification_checked') {
          setPhase('cert_fetch');
        } else if (stepMap[sess.step]) {
          setPhase(stepMap[sess.step]);
        } else if (sess.step === 'ratification_checked') {
          if (sess.flow === 'clave_movil') setPhase('clave_identity');
          else if (sess.flow === 'reference') setPhase('reference_fetch');
        }
      } catch {
        // session expired or invalid — stay on form
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onAnswerChange('aeat_ejercicio', ejercicio);
  }, [ejercicio]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────
  const persistCommonAnswers = useCallback(() => {
    onAnswerChange('aeat_method', method);
    onAnswerChange('connection_sign_in_to_aeat', method);
    onAnswerChange('aeat_nif', nif.trim().toUpperCase());
    onAnswerChange('aeat_ejercicio', ejercicio);
    onAnswerChange('aeat_pdp', AEAT_DEFAULT_PDP);
    onAnswerChange('aeat_iban_last5', ibanLast5.replace(/\D/g, '').slice(0, 5));
  }, [method, nif, ejercicio, ibanLast5, onAnswerChange]);

  const handleFiscalDataError = useCallback((e: unknown) => {
    const p = parseFastApiError(e);
    if (p.code === 'RATIFICATION_REQUIRED_FILE') {
      if (p.ratification_urls && Object.keys(p.ratification_urls).length > 0) {
        setRatificationUrls(p.ratification_urls);
      }
      setPhase('ratification_block');
      setPreConnectModalOpen(true);
      capture('aeat_fiscal_blocked_ratification_5001', { had_urls: Boolean(p.ratification_urls) });
    }
    setError(formatAeatUserFacingError(p));
  }, []);

  const runRatification = useCallback(
    async (sid: string) => {
      const n = nif.trim().toUpperCase();
      const ej = ejercicio;
      return aeatRatification(sid, { nif: n, ejercicio: ej });
    },
    [nif, ejercicio]
  );

  const getRatificationUrlForMethod = useCallback(
    (urls: Record<string, string> | null): string | null => {
      if (!urls) return null;
      if (method === AEAT_METHOD_OPTION_CERTIFICATE) return urls.cert_e || null;
      if (method === AEAT_METHOD_OPTION_CLAVE) return urls.clave_movil || null;
      if (method === AEAT_METHOD_OPTION_REFERENCE) return urls.reference || null;
      return null;
    },
    [method]
  );

  const stopRatPolling = useCallback(() => {
    if (ratPollIntervalRef.current) {
      clearInterval(ratPollIntervalRef.current);
      ratPollIntervalRef.current = null;
    }
    setRatPollingActive(false);
  }, []);

  const proceedAfterRatification = useCallback(async () => {
    if (!aeatSessionId) return;
    try {
      if (method === AEAT_METHOD_OPTION_CERTIFICATE) {
        const res = await aeatFiscalData(aeatSessionId, { ...fiscalPayload(), p12_password: undefined });
        finishSuccess('certificate', res);
      } else if (method === AEAT_METHOD_OPTION_CLAVE) {
        setPreConnectModalOpen(false);
        setPhase('clave_identity');
      } else {
        setPreConnectModalOpen(false);
        setPhase('reference_fetch');
      }
    } catch (e) {
      handleFiscalDataError(e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aeatSessionId, method]);

  const startRatPolling = useCallback(() => {
    if (!aeatSessionId) return;
    ratPollCountRef.current = 0;
    setRatPollingActive(true);
    setRatificationJustPassed(false);

    ratPollIntervalRef.current = setInterval(async () => {
      ratPollCountRef.current += 1;
      if (ratPollCountRef.current > RAT_POLL_MAX_ATTEMPTS) {
        stopRatPolling();
        return;
      }
      try {
        const rat = await runRatification(aeatSessionId);
        setRatificationUrls(rat.ratification_urls || null);
        if (rat.domicilio_ratificado) {
          stopRatPolling();
          setRatificationJustPassed(true);
          capture('aeat_ratification_auto_synced', {
            session_id: aeatSessionId,
            attempts: ratPollCountRef.current,
          });
        }
      } catch {
        // Polling tolerates transient errors -- keep trying until timeout
      }
    }, RAT_POLL_INTERVAL_MS);
  }, [aeatSessionId, runRatification, stopRatPolling]);

  useEffect(() => {
    return () => {
      if (ratPollIntervalRef.current) {
        clearInterval(ratPollIntervalRef.current);
        ratPollIntervalRef.current = null;
      }
    };
  }, []);

  // When ratification passes via polling, auto-proceed after a brief delay
  useEffect(() => {
    if (!ratificationJustPassed) return;
    const timeout = setTimeout(() => {
      void proceedAfterRatification();
    }, 1500);
    return () => clearTimeout(timeout);
  }, [ratificationJustPassed, proceedAfterRatification]);

  // ── Expose Continue → connect to page ──────────────────────────────────────
  useEffect(() => {
    if (connectRef) {
      connectRef.current = {
        promptThenConnect: () => {
          setError(null);
          if (!method) {
            setError(t('aeat.select_method_required'));
            return;
          }
          if (isAeatMethodUploadOrIbanNoLiveConnect(method)) {
            if (method === AEAT_METHOD_OPTION_IBAN) {
              const digits = ibanLast5.replace(/\D/g, '').slice(0, 5);
              if (digits.length !== 5) {
                setError('Enter the last 5 digits of your IBAN.');
                return;
              }
              onNext({
                aeat_method: method,
                connection_sign_in_to_aeat: method,
                aeat_nif: nif.trim().toUpperCase(),
                aeat_ejercicio: ejercicio,
                aeat_pdp: AEAT_DEFAULT_PDP,
                aeat_iban_last5: digits,
              });
              return;
            }
            if (method === AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN) {
              onNext({
                aeat_method: method,
                connection_sign_in_to_aeat: method,
                aeat_nif: nif.trim().toUpperCase(),
                aeat_ejercicio: ejercicio,
                aeat_pdp: AEAT_DEFAULT_PDP,
              });
              return;
            }
          }
          if (!nif.trim()) {
            setError(t('aeat.enter_nif'));
            return;
          }
          void handleConnect();
        },
      };
    }
  }); // runs every render so ref always points to latest closure

  // ── Notify page of phase changes ──────────────────────────────────────────
  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!preConnectModalOpen) return;
    const prevActive = document.activeElement as HTMLElement | null;
    preConnectDialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreConnectModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prevActive?.focus?.();
    };
  }, [preConnectModalOpen]);

  useEffect(() => {
    if (!openHelpLabel) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('[data-aeat-method-help]')) return;
      setOpenHelpLabel(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenHelpLabel(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openHelpLabel]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleConnect = async () => {
    setError(null);
    if (!method) { setError(t('aeat.select_method_required')); return; }
    if (!nif.trim()) { setError(t('aeat.enter_nif')); return; }
    capture('aeat_connect_clicked', { method_label: method });
    setLoading(true);
    try {
      const flow = uiLabelToBffFlow(method);
      const sid = await aeatCreateSession(flow);
      setAeatSessionId(sid);
      onAnswerChange('aeat_session_id', sid);

      // Certificate: no file upload — server cert (AEAT_ALLOW_SERVER_CERT_FALLBACK) handles mTLS
      const rat = await runRatification(sid);
      setRatificationUrls(rat.ratification_urls || null);
      if (!rat.domicilio_ratificado) {
        setPhase('ratification_block');
        setPreConnectModalOpen(true);
        capture('aeat_ratification_blocked', { session_id: sid });
        return;
      }

      if (method === AEAT_METHOD_OPTION_CERTIFICATE) {
        // Fetch fiscal data directly — no password re-entry needed
        const res = await aeatFiscalData(sid, { ...fiscalPayload(), p12_password: undefined });
        finishSuccess('certificate', res);
      } else if (method === AEAT_METHOD_OPTION_CLAVE) {
        setPhase('clave_identity');
      } else {
        setPhase('reference_fetch');
      }
    } catch (e) {
      const p = parseFastApiError(e);
      setError(formatAeatUserFacingError(p) || t('aeat.connection_failed'));
      capture('aeat_connect_failed', { code: p.code, message: p.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRetryRatification = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      const rat = await runRatification(aeatSessionId);
      setRatificationUrls(rat.ratification_urls || null);
      if (!rat.domicilio_ratificado) {
        setError(t('aeat.domicilio_not_ratified'));
        return;
      }
      if (method === AEAT_METHOD_OPTION_CERTIFICATE) {
        const res = await aeatFiscalData(aeatSessionId, { ...fiscalPayload(), p12_password: undefined });
        finishSuccess('certificate', res);
      } else if (method === AEAT_METHOD_OPTION_CLAVE) setPhase('clave_identity');
      else setPhase('reference_fetch');
    } catch (e) {
      setError(formatAeatUserFacingError(parseFastApiError(e)));
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFiscalCert = async () => {
    if (!aeatSessionId) return;
    if (!certPassword.trim()) {
      setError(t('aeat.enter_cert_password'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await aeatFiscalData(aeatSessionId, { ...fiscalPayload(), p12_password: certPassword });
      finishSuccess('certificate', res);
    } catch (e) {
      handleFiscalDataError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleClaveRequestSms = async () => {
    if (!aeatSessionId) return;
    setError(null);
    if (!claveIdentityDocKind) {
      setError('Select the type of document you are using.');
      return;
    }
    if (!nif.trim()) {
      setError(t('aeat.enter_nif'));
      return;
    }
    if (claveIdentityDocKind === 'dni') {
      if (!claveFecha.trim()) {
        setError('Enter the expiry date from your DNI.');
        return;
      }
    } else if (!claveSoporte.trim()) {
      setError('Enter the support number (número de soporte) from your card.');
      return;
    }
    setLoading(true);
    try {
      const res = await aeatClaveStart(aeatSessionId, {
        nif: nif.trim().toUpperCase(),
        fecha:
          claveIdentityDocKind === 'dni' ? claveFecha.trim() || undefined : undefined,
        soporte:
          claveIdentityDocKind === 'nie' ? claveSoporte.trim() || undefined : undefined,
      });
      setClaveMovilHint(res.movil || '');
      setPhase('clave_pin');
      startPinTimer((res as any).sms_requested_at);
      capture('aeat_clave_sms_sent', { mock: res.mock });
    } catch (e) {
      setError(formatAeatUserFacingError(parseFastApiError(e)));
    } finally {
      setLoading(false);
    }
  };

  const handleClaveResendSms = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await aeatClaveResend(aeatSessionId);
      setClaveMovilHint(res.movil || '');
      setClavePin('');
      startPinTimer(res.sms_requested_at);
      capture('aeat_clave_sms_resent', { resend_count: res.resend_count });
    } catch (e) {
      setError(formatAeatUserFacingError(parseFastApiError(e)));
    } finally {
      setLoading(false);
    }
  };

  const handleClaveVerifyAndFetch = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      await aeatClavePin(aeatSessionId, clavePin.trim());
      const res = await aeatFiscalData(aeatSessionId, fiscalPayload());
      finishSuccess('clave_movil', res);
    } catch (e) {
      handleFiscalDataError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleReferenceAndFetch = async () => {
    if (!aeatSessionId) return;
    if (reference.trim().length !== 6) { setError(t('aeat.enter_reference')); return; }
    setError(null);
    setLoading(true);
    try {
      await aeatReference(aeatSessionId, {
        nif: nif.trim().toUpperCase(),
        reference: reference.trim().toUpperCase(),
      });
      const res = await aeatFiscalData(aeatSessionId, fiscalPayload());
      finishSuccess('reference', res);
    } catch (e) {
      handleFiscalDataError(e);
    } finally {
      setLoading(false);
    }
  };

  const finishSuccess = (kind: string, res: FiscalDataResponse) => {
    const parsed = res.parsed;
    persistCommonAnswers();
    onAnswerChange('aeat_connection_completed', true);
    onAnswerChange('aeat_fiscal_data_received', true);
    onAnswerChange('aeat_flow_kind', kind);

    if (!parsed?.prefill_answers) {
      setParseIncompleteReason(res.mock ? 'mock' : 'parse_failed');
      setPrefillSummary(null);
    } else {
      setParseIncompleteReason(null);
    }

    if (parsed?.prefill_answers) {
      const prefillEntries = Object.entries(parsed.prefill_answers);
      for (const [key, value] of prefillEntries) {
        if (value !== null && value !== '' && value !== undefined) {
          const prov = parsed.provenance?.[key];
          if (prov?.source === 'aeat_conflicted') continue;
          onAnswerChange(key, value);
        }
      }
      if (parsed.provenance) onAnswerChange('aeat_import_provenance', parsed.provenance);
      if (parsed.metadata) onAnswerChange('aeat_import_metadata', parsed.metadata);
      if (parsed.discrepancies && parsed.discrepancies.length > 0)
        onAnswerChange('aeat_import_discrepancies', parsed.discrepancies);

      setPrefillSummary({
        fieldCount: parsed.field_count || prefillEntries.length,
        hasName: Boolean(parsed.taxpayer?.first_name),
        hasAddress: Boolean(parsed.address?.street),
        hasIncome: (parsed.income_records?.length ?? 0) > 0,
        discrepancyCount: parsed.discrepancies?.length ?? 0,
      });
      capture('aeat_prefill_applied', {
        kind,
        field_count: parsed.field_count,
        has_taxpayer: Boolean(parsed.taxpayer),
        has_address: Boolean(parsed.address),
        income_count: parsed.income_records?.length ?? 0,
        discrepancy_count: parsed.discrepancies?.length ?? 0,
      });
    }

    setPhase('success');
    capture('aeat_fiscal_success', { kind, had_parsed_prefill: Boolean(parsed?.prefill_answers) });
  };


  // ── Shared input/label styles ─────────────────────────────────────────────
  const labelCls = 'block text-sm font-medium text-[#131315] mb-1';
  const inputCls = 'w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#131315] focus:outline-none focus:ring-2 focus:ring-[#07A2A0]/30';
  const primaryBtnCls = 'w-full px-6 py-2 bg-[#07A2A0] text-white text-sm font-semibold rounded-lg disabled:opacity-50';
  const secondaryBtnCls = 'w-full lg:w-auto px-6 py-2 border border-[#939393] text-[#131315] text-sm font-semibold rounded-lg disabled:opacity-50';
  const errorCls = 'text-red-600 text-sm whitespace-pre-line';

  const showNifAndFiscalScope =
    method === AEAT_METHOD_OPTION_CLAVE || method === AEAT_METHOD_OPTION_REFERENCE;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="w-full flex flex-col items-start gap-10 lg:max-w-[663px]">

      {/* ── FORM PHASE ── */}
      {phase === 'form' && (
        <>
          {/* Header — original design */}
          <div className="w-full flex flex-col gap-4">
            <h1 className="text-lg lg:text-2xl font-medium text-[#131315]">{title}</h1>
            {intro.length > 0 && (
              <div className="w-full flex flex-col gap-2">
                {intro.map((p, idx) => (
                  <p key={`aeat-intro-${idx}`} className="text-[15px] leading-6 text-[#525353] font-medium text-left">
                    {p}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Method buttons — original design */}
          <div className="w-full flex flex-col gap-6">
            <div className="w-full flex flex-col gap-4 overflow-visible">
              {options.map((displayLabel, index) => {
                const canonical = canonicalOptions[index];
                if (!canonical) return null;
                const selected = method === canonical;
                const emoji = getAeatMethodOptionEmoji(canonical);
                const helpLines = getAeatMethodHelpLines(canonical);
                return (
                  <div
                    key={`aeat-method-${index}-${canonical}`}
                    className="flex w-full max-w-full items-center gap-2 overflow-visible"
                  >
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setOpenHelpLabel(null);
                        void handleMethodClick(canonical);
                      }}
                      className={[
                        'flex min-h-[40px] min-w-0 flex-1 items-center justify-center rounded-lg border border-[#939393] px-4 py-2.5 text-center box-border lg:w-[281px] lg:flex-none',
                        selected ? 'bg-[#F0FDFC] shadow-sm' : 'bg-white',
                      ].join(' ')}
                    >
                      <span className="flex min-w-0 items-center justify-center gap-2 text-sm font-semibold text-[#131315] leading-snug">
                        {emoji ? (
                          <span className="shrink-0 self-center text-base leading-none" aria-hidden>
                            {emoji}
                          </span>
                        ) : null}
                        <span className="min-w-0 flex-1 text-center">{displayLabel}</span>
                      </span>
                    </button>
                    {helpLines ? (
                      <div
                        className={['relative flex justify-center overflow-visible', AEAT_METHOD_INFO_COL_CLASS].join(
                          ' '
                        )}
                        data-aeat-method-help
                      >
                        <button
                          type="button"
                          className="inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0.5 text-[#717273] shadow-none outline-none ring-0 [-webkit-tap-highlight-color:transparent] hover:bg-transparent hover:opacity-100 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:bg-transparent active:opacity-100"
                          aria-label={`Help: ${displayLabel}`}
                          aria-expanded={openHelpLabel === canonical}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenHelpLabel((v) => (v === canonical ? null : canonical));
                          }}
                        >
                          <InfoCircle className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
                        </button>
                        <AeatMethodHelpBubble lines={helpLines} open={openHelpLabel === canonical} />
                      </div>
                    ) : (
                      <div className={AEAT_METHOD_INFO_COL_CLASS} aria-hidden />
                    )}
                  </div>
                );
              })}
            </div>

            {/* NIF + fiscal scope: only after Cl@ve or número de referencia is chosen */}
            <div className="w-full lg:w-[310px] flex flex-col gap-4">
              {showNifAndFiscalScope ? (
                <>
                  <div>
                    <label className={labelCls}>NIF / NIE</label>
                    <input
                      type="text"
                      value={nif}
                      onChange={(e) => setNif(e.target.value)}
                      className={inputCls}
                      autoComplete="off"
                    />
                  </div>
                </>
              ) : null}

              {method === AEAT_METHOD_OPTION_IBAN ? (
                <div>
                  <label className={labelCls}>Last 5 digits of your IBAN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={5}
                    value={ibanLast5}
                    onChange={(e) => setIbanLast5(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className={inputCls}
                  />
                </div>
              ) : null}

              {error && <p className={errorCls}>{error}</p>}
            </div>
          </div>
        </>
      )}

      {/* ── RATIFICATION BLOCK ── */}
      {phase === 'ratification_block' && (
        <div className="w-full flex flex-col gap-4">
          {ratificationJustPassed ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm font-medium">
                Fiscal domicile ratified. Fetching your tax data...
              </p>
            </div>
          ) : (
            <>
              <p className="text-[15px] leading-6 text-[#525353] font-medium">
                Your fiscal domicile must be ratified with AEAT before we can download tax data.
                Complete ratification on AEAT, then return here.
              </p>

              {ratPollingActive && (
                <div className="flex items-center gap-2 text-sm text-[#525353]">
                  <svg className="h-4 w-4 animate-spin text-[#07A2A0]" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Checking automatically...
                </div>
              )}

              {error && <p className={errorCls}>{error}</p>}
              <div className="flex flex-col lg:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => { stopRatPolling(); setPhase('form'); }}
                  className={secondaryBtnCls}
                >
                  Edit details
                </button>
                <button
                  type="button"
                  onClick={() => setPreConnectModalOpen(true)}
                  className={[primaryBtnCls, 'lg:flex-1'].join(' ')}
                >
                  View ratification steps
                </button>
                {!ratPollingActive && (
                  <button
                    type="button"
                    onClick={() => void handleRetryRatification()}
                    disabled={busy}
                    className={[secondaryBtnCls, 'lg:flex-1'].join(' ')}
                  >
                    {busy ? 'Checking...' : 'Check again'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CERT FETCH ── */}
      {phase === 'cert_fetch' && (
        <div className="w-full flex flex-col gap-4">
          <p className="text-[15px] leading-6 text-[#525353] font-medium">
            Enter your PKCS#12 password again to unlock the certificate for this request.
            The password is not stored on the server.
          </p>
          <div>
            <label className={labelCls}>PKCS#12 password</label>
            <input
              type="password"
              autoComplete="off"
              className={inputCls}
              value={certPassword}
              onChange={(e) => setCertPassword(e.target.value)}
            />
          </div>
          {error && <p className={errorCls}>{error}</p>}
          <button
            type="button"
            onClick={() => void handleFetchFiscalCert()}
            disabled={busy}
            className={primaryBtnCls}
          >
            {busy ? 'Requesting…' : 'Fetch fiscal data'}
          </button>
          <button type="button" onClick={() => setPhase('form')} className="text-sm text-[#07A2A0] underline text-left">
            Back to form
          </button>
        </div>
      )}

      {/* ── CLAVE IDENTITY ── */}
      {phase === 'clave_identity' && (
        <div className="w-full flex flex-col gap-6 lg:max-w-[663px]">
          <div className="w-full flex flex-col gap-3">
            <p className="text-[15px] leading-6 text-[#525353] font-medium">What type of document do you have?</p>
            <div className="flex flex-col gap-2">
              {(['dni', 'nie'] as const).map((kind) => (
                <label
                  key={kind}
                  className="flex cursor-pointer items-center gap-3 text-sm font-medium text-[#131315]"
                >
                  <input
                    type="radio"
                    name="clave-identity-doc-kind"
                    className="h-4 w-4 shrink-0 border-[#939393] text-[#07A2A0] focus:ring-[#07A2A0]/30"
                    checked={claveIdentityDocKind === kind}
                    onChange={() => {
                      setClaveIdentityDocKind(kind);
                      setError(null);
                      if (kind === 'dni') setClaveSoporte('');
                      else setClaveFecha('');
                    }}
                  />
                  {kind === 'dni' ? 'DNI' : 'NIE'}
                </label>
              ))}
            </div>
          </div>

          {claveIdentityDocKind ? (
            <>
              <div className="flex w-[281px] max-w-full flex-col gap-4">
                <div className="w-full">
                  <label className={labelCls}>{claveIdentityDocKind === 'dni' ? 'DNI' : 'NIE'}</label>
                  <input
                    className={inputCls}
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    placeholder={claveIdentityDocKind === 'dni' ? '9999999R' : 'X1234567P'}
                    autoComplete="off"
                  />
                </div>
                <div className="w-full">
                  <label className={labelCls}>
                    {claveIdentityDocKind === 'dni' ? 'Expiry date' : 'Support number'}
                  </label>
                  <input
                    className={inputCls}
                    value={claveIdentityDocKind === 'dni' ? claveFecha : claveSoporte}
                    onChange={(e) =>
                      claveIdentityDocKind === 'dni'
                        ? setClaveFecha(e.target.value)
                        : setClaveSoporte(e.target.value)
                    }
                    placeholder={
                      claveIdentityDocKind === 'dni'
                        ? t('placeholders.date_dmy_dash')
                        : 'E00000000'
                    }
                    autoComplete="off"
                  />
                </div>
              </div>

              <figure className="m-0 h-[175px] w-full shrink-0 overflow-hidden rounded-xl bg-[#F9FAFB]">
                <img
                  src={claveIdentityDocKind === 'dni' ? '/img/DNI_example.png' : '/img/NIE_example.png'}
                  alt={
                    claveIdentityDocKind === 'dni'
                      ? 'Example Spanish DNI highlighting document number and expiry date'
                      : 'Example Spanish residency card highlighting NIE and support number'
                  }
                  className="block h-full w-full object-contain object-left"
                />
              </figure>

              {error && <p className={errorCls}>{error}</p>}

              <button
                type="button"
                onClick={() => void handleClaveRequestSms()}
                disabled={busy}
                className={primaryBtnCls}
              >
                {busy ? 'Sending…' : 'Request SMS code'}
              </button>
            </>
          ) : (
            error && <p className={errorCls}>{error}</p>
          )}
        </div>
      )}

      {/* ── CLAVE PIN ── */}
      {phase === 'clave_pin' && (
        <div className="w-full flex flex-col gap-4">
          {claveMovilHint && (
            <p className="text-[15px] leading-6 text-[#525353] font-medium">
              Code sent to mobile ending in {claveMovilHint}
            </p>
          )}
          {pinSecondsLeft !== null && (
            <p className={`text-sm ${pinSecondsLeft > 0 ? 'text-[#525353]' : 'text-red-600 font-medium'}`}>
              {pinSecondsLeft > 0
                ? `PIN expires in ${Math.floor(pinSecondsLeft / 60)}:${String(pinSecondsLeft % 60).padStart(2, '0')}`
                : 'PIN expired — request a new one'}
            </p>
          )}
          <div>
            <label className={labelCls}>SMS PIN</label>
            <input
              type="password"
              className={inputCls}
              value={clavePin}
              onChange={(e) => setClavePin(e.target.value)}
              autoComplete="one-time-code"
            />
          </div>
          {error && <p className={errorCls}>{error}</p>}
          <button
            type="button"
            onClick={() => void handleClaveVerifyAndFetch()}
            disabled={busy || pinSecondsLeft === 0}
            className={primaryBtnCls}
          >
            {busy ? 'Verifying…' : 'Verify PIN and fetch fiscal data'}
          </button>
          <button
            type="button"
            onClick={() => void handleClaveResendSms()}
            disabled={busy}
            className="text-sm text-[#07A2A0] underline text-left"
          >
            Resend SMS code
          </button>
        </div>
      )}

      {/* ── REFERENCE FETCH ── */}
      {phase === 'reference_fetch' && (
        <div className="w-full flex flex-col gap-4">
          <div>
            <label className={labelCls}>Número de referencia (6 alphanumeric characters)</label>
            <input
              className={[inputCls, 'uppercase font-mono tracking-widest'].join(' ')}
              value={reference}
              maxLength={6}
              pattern="[A-Z0-9]{6}"
              placeholder={t('placeholders.nif_example')}
              onChange={(e) =>
                setReference(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())
              }
            />
            <p className="text-xs text-[#525353] mt-1">Letters A-Z and digits 0-9 only</p>
          </div>
          {error && <p className={errorCls}>{error}</p>}
          <button
            type="button"
            onClick={() => void handleReferenceAndFetch()}
            disabled={busy}
            className={primaryBtnCls}
          >
            {busy ? 'Validating…' : 'Validate reference and fetch fiscal data'}
          </button>
        </div>
      )}

      {/* ── SUCCESS ── */}
      {phase === 'success' && (
        <div className="w-full flex flex-col gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm font-medium">
              AEAT connection completed. Fiscal data was requested successfully.
            </p>
          </div>

          {parseIncompleteReason === 'parse_failed' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-950">
              <p className="font-medium mb-1">Automatic import incomplete</p>
              <p>
                We received a response from AEAT but could not map it into questionnaire fields.
                Your answers were not pre-filled; you can still continue and enter details manually.
              </p>
            </div>
          )}

          {parseIncompleteReason === 'mock' && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-800">
              Mock fiscal mode: prefill from the downloaded file is disabled. Continue to enter data manually.
            </div>
          )}

          {prefillSummary && prefillSummary.fieldCount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-900 text-sm font-medium mb-2">
                {prefillSummary.fieldCount} fields imported from AEAT
              </p>
              <ul className="text-sm text-blue-800 flex flex-col gap-1 list-disc list-inside">
                {prefillSummary.hasName && <li>Identity and personal details</li>}
                {prefillSummary.hasAddress && <li>Fiscal address</li>}
                {prefillSummary.hasIncome && <li>Employment income and withholdings</li>}
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                Imported values will appear pre-filled in the questionnaire. You can review and change them on each screen.
              </p>
            </div>
          )}

          {prefillSummary && prefillSummary.discrepancyCount > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <p className="text-amber-900 text-sm font-medium mb-1">
                {prefillSummary.discrepancyCount} field{prefillSummary.discrepancyCount > 1 ? 's' : ''} differ from your previous answers
              </p>
              <p className="text-xs text-amber-700">
                AEAT returned values that differ from what you entered earlier. Your existing answers have been kept.
                Please review these fields as you continue.
              </p>
            </div>
          )}

          <p className="text-sm text-[#525353]">Click <strong>Continue</strong> in the navigation bar below to proceed.</p>
        </div>
      )}
    </div>

    {preConnectModalOpen ? (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        onClick={() => { if (!ratPollingActive) setPreConnectModalOpen(false); }}
        role="presentation"
      >
        <div
          ref={preConnectDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="aeat-preconnect-title"
          tabIndex={-1}
          className="w-full max-w-[450px] rounded-2xl bg-white p-6 shadow-lg outline-none focus:ring-2 focus:ring-[#07A2A0]/40"
          onClick={(e) => e.stopPropagation()}
        >
          {ratificationJustPassed ? (
            <div className="flex flex-col gap-4 items-center">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-base font-medium text-center text-[#131315] leading-snug">
                Fiscal domicile ratified
              </h2>
              <p className="text-sm text-center text-[#525353]">
                Your address has been confirmed with AEAT. Proceeding to fetch your fiscal data...
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <h2 id="aeat-preconnect-title" className="text-base font-medium text-center text-[#131315] leading-snug">
                One quick step on the Tax Agency website
              </h2>
              <div className="flex flex-col gap-3 text-sm leading-6 text-[#525353]">
                <p className="text-center">
                  To access your tax data, Spain&apos;s Tax Agency (AEAT) requires you to confirm your registered address.
                  It only takes a few seconds.
                </p>
                <div className="border border-[#E2E8F0]"></div>
                <p className="font-normal text-xs text-[#717273]">Once on the AEAT website:</p>
                <ol className="list-decimal list-inside flex flex-col gap-2 pl-1 text-xs text-[#717273]">
                  <li>Check that your address is correct</li>
                  <li>Select &quot;Titularidad&quot;</li>
                  <li>Tap &quot;Rectificar&quot;</li>
                </ol>
              </div>

              {ratPollingActive ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-center gap-2 text-sm text-[#525353]">
                    <svg className="h-4 w-4 animate-spin text-[#07A2A0]" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Checking if ratification is complete...
                  </div>
                  <p className="text-xs text-center text-[#717273]">
                    Complete the steps on the AEAT website and we&apos;ll detect it automatically.
                  </p>
                </div>
              ) : !ratPollingActive && ratPollCountRef.current >= RAT_POLL_MAX_ATTEMPTS ? (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-center text-amber-700">
                    Automatic check timed out. Complete ratification on AEAT, then check manually.
                  </p>
                  <button
                    type="button"
                    className="w-full h-11 rounded-lg bg-[#6ECFCF] px-4 text-[13px] font-medium text-[#131315] hover:bg-[#069694] transition-colors focus:outline-none focus:ring-2 focus:ring-[#07A2A0]/40 disabled:opacity-50"
                    disabled={busy}
                    onClick={() => {
                      setPreConnectModalOpen(false);
                      void handleRetryRatification();
                    }}
                  >
                    {busy ? 'Checking...' : 'Check again'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="w-full h-11 rounded-lg bg-[#6ECFCF] px-4 text-[13px] font-medium text-[#131315] hover:bg-[#069694] transition-colors focus:outline-none focus:ring-2 focus:ring-[#07A2A0]/40 disabled:opacity-50"
                  disabled={busy}
                  onClick={() => {
                    const url = getRatificationUrlForMethod(ratificationUrls);
                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                    startRatPolling();
                  }}
                >
                  Take me to AEAT
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    ) : null}
    </>
  );
}
